<template>
  <div class="min-h-screen bg-gray-50">
    <!-- Hero Section -->
    <section class="relative bg-gradient-to-br from-sky-200 via-teal-200 to-emerald-200 overflow-hidden">
      <div class="absolute inset-0 bg-white/30 backdrop-blur-sm"></div>
      <div class="relative container mx-auto px-4 sm:px-6 lg:px-8 max-w-screen-xl py-12 sm:py-16 lg:py-20">
        <div class="grid lg:grid-cols-2 gap-12 items-center">
          <!-- Left Content -->
          <div class="text-gray-800 lg:pr-12">
            <h1 class="text-3xl sm:text-4xl lg:text-5xl font-bold mb-6 text-gray-800 leading-tight">
              智光幼兒園安全接送<br />
              <span class="text-yellow-500 block pt-2">門禁系統</span>
            </h1>

            <p class="text-base sm:text-lg lg:text-xl mb-8 text-gray-700 leading-relaxed max-w-xl">
              專業幼兒園安全接送管理系統，採用ID/IC卡刷卡技術，結合照片顯示核對功能，確保幼兒接送安全。系統提供完整的幼兒資料管理、人事考勤及即時監控功能。
            </p>
          </div>

          <!-- Right Image -->
          <div class="flex justify-center lg:justify-end">
            <div class="relative w-full max-w-xl">
              <img src="@/assets/img/product4-1.jpg" alt="智光幼兒園安全接送門禁系統"
                class="rounded-3xl shadow-2xl object-cover w-full h-auto" />
              <!-- Decorative Circles -->
              <div class="absolute -top-4 -right-4 w-20 h-20 bg-yellow-300 rounded-full opacity-20"></div>
              <div class="absolute -bottom-4 -left-4 w-16 h-16 bg-teal-300 rounded-full opacity-20"></div>
            </div>
          </div>
        </div>
      </div>

      <!-- Decorative Elements -->
      <div class="absolute top-0 right-0 w-1/3 h-full opacity-10">
        <div class="w-full h-full bg-gradient-to-l from-white to-transparent"></div>
      </div>
    </section>

    <!-- 功能特色區 -->
    <section class="py-12 bg-white">
      <div class="container mx-auto px-4 sm:px-6 lg:px-8">
        <h2 class="text-3xl sm:text-4xl font-bold text-gray-800 mb-12 text-center">
          功能特色
        </h2>
        <div class="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-10">
          <div v-for="(feature, index) in features" :key="index"
            class="flex flex-col items-start gap-4 p-4 border rounded-lg shadow-sm hover:shadow-md transition-shadow duration-300">
            <div :class="['w-10 h-10 rounded-full flex items-center justify-center flex-shrink-0', feature.iconBg]">
              <svg class="w-5 h-5 text-white" fill="none" stroke="currentColor" stroke-width="2" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" :d="feature.iconPath" />
              </svg>
            </div>
            <h3 class="text-lg font-semibold text-gray-800 mb-1">{{ feature.title }}</h3>
            <p class="text-gray-600">{{ feature.summary }}</p>
            <button @click="toggle(index)" class="text-yellow-500 mt-1 underline text-sm self-start">
              {{ showMoreList[index] ? '收起' : '更多' }}
            </button>
            <p v-if="showMoreList[index]" class="text-gray-600 mt-2">{{ feature.details }}</p>
          </div>
        </div>
      </div>
    </section>


    <!-- 系統效益區 -->
    <section class="py-12 bg-gray-50">
      <div class="p-8 sm:p-12">
        <h2 class="text-3xl sm:text-4xl font-bold text-gray-800 mb-8 text-center">
          系統效益
        </h2>
        <div class="max-w-5xl mx-auto">
          <div class="grid md:grid-cols-3 gap-8 mb-8">

            <!-- 效益 1：安全即時接送 -->
            <div class="text-center">
              <div class="w-16 h-16 bg-emerald-500 rounded-full flex items-center justify-center mx-auto mb-4">
                <svg class="w-8 h-8 text-white" fill="none" stroke="currentColor" stroke-width="2" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" d="M5 13l4 4L19 7" />
                </svg>
              </div>
              <h3 class="text-xl font-semibold text-gray-800 mb-2">安全即時接送</h3>
              <p class="text-gray-600">刷卡同時顯示家長與幼兒照片，門衛即時核對身份，有效杜絕接錯人風險。</p>
            </div>

            <!-- 效益 2：數據化管理 -->
            <div class="text-center">
              <div class="w-16 h-16 bg-sky-500 rounded-full flex items-center justify-center mx-auto mb-4">
                <svg class="w-8 h-8 text-white" fill="none" stroke="currentColor" stroke-width="2" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" d="M9 17v-2a4 4 0 014-4h4" />
                  <path stroke-linecap="round" stroke-linejoin="round"
                    d="M13 7H7a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-6" />
                </svg>
              </div>
              <h3 class="text-xl font-semibold text-gray-800 mb-2">數據化管理</h3>
              <p class="text-gray-600">精準記錄幼兒與教職員考勤、進出紀錄，統計分析一目了然。</p>
            </div>

            <!-- 效益 3：提升校園運營效率 -->
            <div class="text-center">
              <div class="w-16 h-16 bg-yellow-500 rounded-full flex items-center justify-center mx-auto mb-4">
                <svg class="w-8 h-8 text-white" fill="none" stroke="currentColor" stroke-width="2" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" d="M3 10h4l3 10h8l3-10h4" />
                  <path stroke-linecap="round" stroke-linejoin="round" d="M16 21a4 4 0 01-8 0" />
                </svg>
              </div>
              <h3 class="text-xl font-semibold text-gray-800 mb-2">提升校園運營效率</h3>
              <p class="text-gray-600">幫助幼兒園即時掌握在園人數，優化食堂配餐與教學安排。</p>
            </div>
          </div>
        </div>
      </div>
    </section>


    <!-- CTA Section -->
    <section class="py-12 bg-white">
      <div class="container mx-auto px-6 text-center">
        <div class="max-w-4xl mx-auto">
          <h2 class="text-4xl font-bold text-gray-800 mb-6">
            立即體驗智光幼兒園安全接送門禁系統
          </h2>
          <p class="text-xl text-gray-700 mb-8 max-w-2xl mx-auto">
            專業團隊提供完整的產品諮詢與技術支援，協助您快速部署
          </p>
          <div class="flex flex-col sm:flex-row gap-4 justify-center">
            <router-link to="/contactus">
              <button aria-label="聯絡我們瞭解更多"
                class="bg-yellow-400 hover:bg-yellow-500 text-gray-900 px-8 py-4 rounded-lg font-bold text-lg hover:scale-105 transition-all duration-300 shadow-lg hover:shadow-xl">
                聯絡我們瞭解更多
              </button>
            </router-link>
          </div>
        </div>
      </div>
    </section>
  </div>
</template>

<script setup>
import { ref } from 'vue'

const features = [
  {
    iconBg: 'bg-sky-500',
    iconPath: 'M10 2v5.586l6 6V20a1 1 0 01-1.447.894L9 18.618V14a1 1 0 00-1-1H4a1 1 0 00-1 1v6.618l-5.553 2.276A1 1 0 01-4 20v-6.414l6-6V2a1 1 0 011-1h6a1 1 0 011 1z',
    title: '幼兒接送管理',
    summary: '方便快捷的ID/IC卡刷卡接送，提升接送效率與安全。',
    details: '標準型幼教安全接送系統使幼兒上學簽到、放學接領的速度和效率明顯提高，並且憑卡接送，提高了幼兒接送的正確率，使幼兒來的放心、回的安全，增強了家長對幼稚園的信任。',
  },
  {
    iconBg: 'bg-amber-500',
    iconPath: 'M9 5H7a2 2 0 00-2 2v10a2 2 0 002 2h8a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-6 9l2 2 4-4',
    title: '幼兒資料管理',
    summary: '完整記錄幼兒姓名、班級、家長資訊等資料。',
    details: '系統記錄幼兒姓名、年齡、班級、家長資訊等完整資料檔案，方便隨時查詢和管理。',
  },
  {
    iconBg: 'bg-purple-500',
    iconPath: 'M12 14c2.21 0 4-1.79 4-4s-1.79-4-4-4-4 1.79-4 4 1.79 4 4 4zm0 2c-2.67 0-8 1.34-8 4v2h16v-2c0-2.66-5.33-4-8-4z',
    title: '人事管理',
    summary: '全面管理教職工資料、崗位與考勤，提升園務效率。',
    details: '系統詳盡記錄教職工崗位、部門和個人資料，並且管理考勤，促使人事管理更規範合理，支援考勤報表列印。',
  },
  {
    iconBg: 'bg-teal-500',
    iconPath: 'M15 10l4.553-2.276A1 1 0 0121 8.618v6.764a1 1 0 01-1.447.894L15 14M5 18h8a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v8a2 2 0 002 2z',
    title: '考勤管理',
    summary: '刷卡簽到管理，消除人情考勤弊病。',
    details: '系統客觀真實記錄教職工到園時間，可列印詳細考勤報表，提升考勤管理效率和公平性。',
  },
];

const showMoreList = ref(features.map(() => false))

function toggle(index) {
  showMoreList.value[index] = !showMoreList.value[index]
}
</script>

<style lang="scss" scoped>
// 修正 z-index 層級問題
.text-gray-800 {
  position: relative;
  z-index: 1;
}

// Hero section 的背景層級
.absolute.inset-0 {
  z-index: 0;
}

// 裝飾元素層級
.absolute.top-0.right-0 {
  z-index: 0;
}

section.bg-white {
  position: relative;
  z-index: 1;
}

// 卡片 hover 效果
.group:hover .w-6.h-6 {
  transform: scale(1.1);
}

// 響應式設計
@media (max-width: 768px) {
  .grid.md\\:grid-cols-2 {
    grid-template-columns: 1fr;
  }

  .grid.md\\:grid-cols-3 {
    grid-template-columns: 1fr;
    gap: 1.5rem;
  }

  .text-3xl.sm\\:text-4xl.lg\\:text-6xl {
    font-size: 2rem;
  }

  .text-base.sm\\:text-lg.lg\\:text-xl {
    font-size: 1rem;
  }
}

// 動畫效果
@keyframes fadeInUp {
  from {
    opacity: 0;
    transform: translateY(30px);
  }

  to {
    opacity: 1;
    transform: translateY(0);
  }
}

.animate-fadeInUp {
  animation: fadeInUp 0.6s ease-out;
}

// 按鈕 hover 效果
button {
  transition: all 0.3s ease;
}

button:hover {
  transform: translateY(-2px);
}

// 響應式按鈕
@media (max-width: 640px) {
  .flex.flex-col.sm\\:flex-row {
    flex-direction: column;
  }

  button {
    width: 100%;
    margin-bottom: 0.5rem;
  }
}

// 圖片容器樣式
img {
  transition: transform 0.3s ease;
}

img:hover {
  transform: scale(1.02);
}

// 功能卡片 hover 效果
.flex.items-start.gap-4 {
  transition: all 0.3s ease;
  padding: 1rem;
  border-radius: 1rem;
}

.flex.items-start.gap-4:hover {
  background-color: rgba(59, 130, 246, 0.05);
  transform: translateY(-2px);
}

// 漸層背景
.bg-gradient-to-br.from-sky-50.to-emerald-50 {
  background: linear-gradient(135deg, #f0f9ff 0%, #ecfdf5 100%);
}
</style>